/**
 * Abwesenheiten (Urlaub, Krankheit) aus dem epcan-Intranet – Zusatzpaket Z-01.
 *
 * ## Warum das hier liegt und nicht im Backend
 *
 * Dieselben Fragen werden an zwei Enden gestellt: der Server sortiert den
 * Zuweisungs-Vorschlag danach, der Browser färbt Zeitleiste und Kapazität. Zwei
 * Fassungen von „ist die Person an diesem Tag da?" würden auseinanderlaufen –
 * und die Oberfläche würde dann etwas anderes behaupten als die Prozedur, die
 * entscheidet. Genau die Falle stand schon in
 * [E-26](../../../docs/ENTSCHEIDUNGEN.md).
 *
 * ## Tagesschlüssel, keine Zeitpunkte
 *
 * Eine Abwesenheit ist ein **Kalenderbereich**, kein Zeitraum auf der Uhr. „Vom
 * 01.09. bis 14.09." bedeutet in jeder Zeitzone dasselbe. Deshalb stehen `von`
 * und `bis` als `JJJJ-MM-TT` in der Datenbank und hier – und deshalb ist der
 * Vergleich ein **Zeichenkettenvergleich**: ISO-Datumsschlüssel sortieren
 * lexikographisch genau wie chronologisch.
 *
 * Das ist kein Trick, sondern die Anwendung von [E-17](../../../docs/ENTSCHEIDUNGEN.md)
 * auf den einfachsten Fall: wo kein Zeitpunkt gebraucht wird, gibt es auch keine
 * Zeitzone, die falsch sein kann. Ein `Date` daraus zu machen wäre der Weg,
 * über den sich der Fehler einschleicht.
 *
 * **`bis` ist einschließlich.** So liefert es das Intranet („von 01.09. bis
 * 14.09." sind 14 Tage), und so wird es hier auch behandelt.
 */

import { istWochenende } from './zeit'

/** Quell-Kennung des Intranets in `source` und `person_external_id`. */
export const INTRANET_QUELLE = 'intranet'

/**
 * Eine Abwesenheit, wie Work sie führt.
 *
 * Bewusst **ohne Art** (Urlaub / Krankheit): die Intranet-Schnittstelle liefert
 * sie nicht mit. Das ist kein Mangel, sondern für diesen Zweck die bessere
 * Auskunft – für die Arbeitssteuerung zählt „ist da / ist nicht da", und eine
 * Krankmeldung ist eine Gesundheitsangabe, die in einem Aufgabenwerkzeug nichts
 * zu suchen hat. Wer die Art bräuchte, müsste sie zuerst fachlich begründen.
 */
export interface Abwesenheit {
  id: string
  /**
   * Person in Work – `null`, solange der Intranet-Mitarbeiter niemandem
   * zugeordnet ist. Die Abwesenheit wird trotzdem gespeichert und angezeigt:
   * eine fehlende Zuordnung soll sichtbar sein, nicht die Daten verschlucken.
   */
  personId: string | null
  /** Name, wie das Intranet ihn führt – trägt die Anzeige ohne Zuordnung. */
  name: string
  /** Erster Tag, `JJJJ-MM-TT`. */
  von: string
  /** Letzter Tag, **einschließlich**. */
  bis: string
  halberTagStart: boolean
  halberTagEnde: boolean
  /**
   * Zustand im Intranet. Bekannt ist `approved`; andere Werte kommen vor und
   * werden nicht auf ein Enum gezwungen – ein unbekannter Wert soll durchkommen
   * und als „beantragt" gelten, nicht den Abgleich abbrechen.
   */
  status: string
}

/** Genehmigt heißt verbindlich. Alles andere kann sich noch ändern. */
export function istVerbindlich(abwesenheit: Pick<Abwesenheit, 'status'>): boolean {
  return abwesenheit.status === 'approved'
}

/** Liegt der Tag im Bereich? Einschließlich beider Ränder. */
export function umfasst(abwesenheit: Pick<Abwesenheit, 'von' | 'bis'>, tag: string): boolean {
  return abwesenheit.von <= tag && tag <= abwesenheit.bis
}

/** Ist an diesem Tag nur ein halber Tag betroffen? */
export function istHalberTag(abwesenheit: Abwesenheit, tag: string): boolean {
  if (!umfasst(abwesenheit, tag)) return false
  if (abwesenheit.halberTagStart && tag === abwesenheit.von) return true
  return abwesenheit.halberTagEnde && tag === abwesenheit.bis
}

/**
 * Die Abwesenheit, die an diesem Tag gilt – oder `null`.
 *
 * **Verbindliche gewinnen.** Liegt über demselben Tag ein genehmigter Urlaub und
 * ein beantragter, ist die Person weg; die schwächere Aussage darf die stärkere
 * nicht verdecken. Bei Gleichstand gewinnt der ganze Tag über den halben, aus
 * demselben Grund.
 */
export function abwesenheitAm(liste: readonly Abwesenheit[], tag: string): Abwesenheit | null {
  let treffer: Abwesenheit | null = null
  for (const a of liste) {
    if (!umfasst(a, tag)) continue
    if (!treffer) {
      treffer = a
      continue
    }
    const besser =
      (istVerbindlich(a) ? 2 : 0) + (istHalberTag(a, tag) ? 0 : 1) >
      (istVerbindlich(treffer) ? 2 : 0) + (istHalberTag(treffer, tag) ? 0 : 1)
    if (besser) treffer = a
  }
  return treffer
}

/**
 * Die nächste Abwesenheit, die **nach** diesem Tag beginnt.
 *
 * Für den Zuweisungs-Vorschlag: wer heute da ist, aber ab Montag drei Wochen
 * weg, ist eine andere Wahl als wer durchgehend da ist – ohne dass ihm deshalb
 * nichts zugeteilt werden darf (Entscheidung Nils, 31.07.2026).
 */
export function naechsteAbwesenheit(
  liste: readonly Abwesenheit[],
  abTag: string,
): Abwesenheit | null {
  let treffer: Abwesenheit | null = null
  for (const a of liste) {
    if (a.von <= abTag) continue
    if (!treffer || a.von < treffer.von) treffer = a
  }
  return treffer
}

/**
 * **Was von einem Arbeitstag übrig bleibt, wenn man weg ist**
 * ([Z-19](../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.5, 02.10.2026).
 *
 * Bis hierher wusste der Tagesplan von Abwesenheit nichts. Er rechnete den Urlaubstag wie jeden
 * anderen durch: acht Stunden, Grundlast ab, Aufgaben hinein – und sah dabei aus wie jeder
 * andere Plan. Das ist der unangenehme Fall. Ein Plan, der sichtbar nichts weiß, lässt einen
 * selber nachdenken; einer, der **glaubwürdig** falsch liegt, nicht.
 *
 * ## Wie der halbe Tag gelesen wird
 *
 * Das Intranet liefert `halber_tag_start` und `halber_tag_ende`, aber **keine Uhrzeit**. Gelesen
 * wird das hier so, wie es gemeint ist: Beginnt die Abwesenheit mittags, ist der **Vormittag**
 * noch Arbeit; endet sie mittags, der **Nachmittag**. Das ist eine Auslegung, keine Angabe aus
 * der Schnittstelle – sie steht deshalb an **einer** Stelle, damit eine spätere Sitzung sie hier
 * ändern kann und nicht an fünf.
 *
 * Fällt beides auf denselben Tag (eine Abwesenheit von einem halben Tag, die mittags beginnt
 * **und** mittags endet), bleibt nichts übrig – zwei halbe Tage sind einer.
 */
export interface Tagesabwesenheit {
  /** Welcher Teil des Arbeitstags noch Arbeitszeit ist. */
  verbleibt: 'nichts' | 'vormittag' | 'nachmittag'
  /** Letzter Tag der Abwesenheit – trägt den Satz „noch bis Freitag". */
  bis: string
  /** Genehmigt oder nur beantragt? Beantragtes zählt mit, sagt es aber dazu. */
  verbindlich: boolean
}

/**
 * Die Abwesenheit dieser Person an diesem Tag – oder `null`, wenn sie da ist.
 *
 * Nimmt `abwesenheitAm`, das bereits die stärkere Aussage gewinnen lässt (genehmigt vor
 * beantragt, ganzer Tag vor halbem), und übersetzt sie in das, was der Plan braucht.
 */
export function tagesabwesenheit(
  liste: readonly Abwesenheit[],
  tag: string,
): Tagesabwesenheit | null {
  const treffer = abwesenheitAm(liste, tag)
  if (!treffer) return null
  const beginntMittags = treffer.halberTagStart && tag === treffer.von
  const endetMittags = treffer.halberTagEnde && tag === treffer.bis
  const verbleibt =
    beginntMittags && endetMittags
      ? 'nichts'
      : beginntMittags
        ? 'vormittag'
        : endetMittags
          ? 'nachmittag'
          : 'nichts'
  return { verbleibt, bis: treffer.bis, verbindlich: istVerbindlich(treffer) }
}

/** Der Anteil eines Arbeitstags, der nach Abzug der Abwesenheit bleibt: `1`, `0.5` oder `0`. */
export function anteilDesTages(abwesend: Tagesabwesenheit | null): number {
  if (!abwesend) return 1
  return abwesend.verbleibt === 'nichts' ? 0 : 0.5
}

/** Alle Tage aus `tage`, an denen die Person weg war. Für die Zeitleiste. */
export function abwesendeTage(liste: readonly Abwesenheit[], tage: readonly string[]): Set<string> {
  const treffer = new Set<string>()
  for (const tag of tage) {
    if (abwesenheitAm(liste, tag)) treffer.add(tag)
  }
  return treffer
}

/**
 * **Fehltage im Zeitraum – die Zahl, die eine Null erst lesbar macht**
 * (Nils, 19.08.2026: *„Können wir die Auswertung noch erweitern, dass man Fehltage
 * sieht?"*).
 *
 * Die Zeitleiste zeigt Abwesenheit längst als Schraffur, aber die Personen-Tabelle
 * daneben zeigt nur Zahlen – und *„0 erledigt"* liest sich dort wie ein Urteil. Mit
 * dieser Spalte steht neben der Null, ob überhaupt jemand da war.
 *
 * ## Drei Festlegungen
 *
 * **Nur Werktage.** Ein Urlaub über zwei Wochen sind 10 Fehltage, nicht 14 – sonst
 * hinge die Zahl daran, ob ein Wochenende hineinfällt.
 *
 * **Halbe Tage zählen halb.** Das Intranet führt sie eigens; sie zu runden hieße, die
 * feinere Angabe wegzuwerfen, die man extra geholt hat.
 *
 * **Beantragtes zählt mit.** `abwesenheitAm` bevorzugt das Verbindliche, nimmt aber
 * auch Beantragtes – wer im Rückblick zählt, sieht Tage, die längst vorbei sind; dass
 * ein Antrag von damals nie genehmigt wurde, ist die seltenere Erklärung als ein
 * Nachtrag, der nie kam.
 *
 * > **Feiertage kennt Work nicht.** Die Intranet-Schnittstelle liefert sie nicht mit,
 * > und ein fest verdrahteter Kalender wäre geraten. Ein Feiertag zählt hier deshalb
 * > **nicht** als Fehltag – die Zahl ist damit eher zu niedrig als zu hoch. Wer sie
 * > gegen Anwesenheit rechnet, muss das wissen; deshalb steht es auch an der Tabelle.
 */
export function fehltageZaehlen(liste: readonly Abwesenheit[], tage: readonly string[]): number {
  let summe = 0
  for (const tag of tage) {
    if (istWochenende(tag)) continue
    const treffer = abwesenheitAm(liste, tag)
    if (!treffer) continue
    summe += istHalberTag(treffer, tag) ? 0.5 : 1
  }
  return summe
}

/** Was für eine Person gerade und als Nächstes gilt. */
export interface AbwesenheitsLage {
  heute: Abwesenheit | null
  naechste: Abwesenheit | null
}

/**
 * Lage je Person, in einem Durchgang.
 *
 * Personenlos (`personId === null`) gelistete Abwesenheiten fallen heraus – sie
 * gehören zu einem Intranet-Mitarbeiter ohne Zuordnung und lassen sich keiner
 * Kapazität zurechnen. Sichtbar sind sie trotzdem, über die Zuordnungs-Ansicht.
 */
export function lageJePerson(
  liste: readonly Abwesenheit[],
  heute: string,
): Map<string, AbwesenheitsLage> {
  const jePerson = new Map<string, Abwesenheit[]>()
  for (const a of liste) {
    if (!a.personId) continue
    const bisher = jePerson.get(a.personId)
    if (bisher) bisher.push(a)
    else jePerson.set(a.personId, [a])
  }

  const lage = new Map<string, AbwesenheitsLage>()
  for (const [personId, eigene] of jePerson) {
    lage.set(personId, {
      heute: abwesenheitAm(eigene, heute),
      naechste: naechsteAbwesenheit(eigene, heute),
    })
  }
  return lage
}

/* ── Namensabgleich für die Zuordnung ─────────────────────────────────── */

/**
 * Titel und Namenszusätze, die in einem Personalsystem stehen und in GitLab
 * nicht. Sie würden jeden Vergleich verhindern, tragen aber keine Unterscheidung.
 */
const ZUSAETZE = new Set(['dr', 'prof', 'dipl', 'ing', 'mba', 'msc', 'bsc', 'med'])

/**
 * Name in vergleichbare Bestandteile zerlegen – **in der gelesenen Reihenfolge**.
 *
 * Was hier passiert und warum:
 *
 * - `Mustermann, Max` → `['max', 'mustermann']`. Personalsysteme schreiben
 *   „Nachname, Vorname", Entwicklerwerkzeuge „Vorname Nachname". Ohne diesen
 *   Tausch fände der Abgleich im Regelfall nichts.
 * - Umlaute und Akzente werden gefaltet (`Müller` → `muller`). GitLab-Konten
 *   tragen oft die entschärfte Schreibweise, das Personalsystem die richtige.
 * - Titel fallen weg, Bindestriche werden zu Wortgrenzen.
 */
export function namensteile(name: string): string[] {
  const [hinten, vorne] = name.split(',')
  const gedreht = vorne ? `${vorne} ${hinten}` : name

  return (
    gedreht
      .normalize('NFD')
      // Kombinierende Akzente entfernen; ß und die skandinavischen Zeichen
      // zerlegt NFD nicht, die kommen darunter einzeln.
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/ø/gi, 'o')
      .replace(/æ/gi, 'ae')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((teil) => teil.length > 0 && !ZUSAETZE.has(teil))
  )
}

/**
 * Vergleichsschlüssel: Bestandteile **sortiert**.
 *
 * Sortiert, damit „Max Mustermann" und „Mustermann Max" denselben Schlüssel
 * ergeben – die Reihenfolge ist zwischen den Systemen nicht verlässlich, der
 * Namensbestand schon.
 */
export function namensSchluessel(name: string): string {
  return namensteile(name).sort().join(' ')
}

/**
 * Zweite Lesart desselben Namens: Umlaute als **Umschrift** statt akzentlos.
 *
 * `namensteile` liefert für `Müller` das akzentlose `muller`. Im Personalsystem
 * steht aber `Müller`, im GitLab-Konto oft `Mueller` – und `mueller` ist gegen
 * `muller` ungleich. Das ist kein Randfall: bei deutschen Namen ist es der
 * Normalfall, und ohne diese zweite Lesart fände die Erkennung genau die
 * Personen nicht, für die sie gebaut ist.
 *
 * Umgekehrt beide Formen auf eine zu quetschen (`ue` → `u`) wäre grober:
 * `Michael` würde zu `michal`. Zwei genaue Lesarten sind besser als eine
 * ungenaue.
 */
function namensteileUmschrift(name: string): string[] {
  return namensteile(
    name
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/Ä/g, 'Ae')
      .replace(/Ö/g, 'Oe')
      .replace(/Ü/g, 'Ue'),
  )
}

/** Alle Schlüssel, unter denen ein Name gefunden werden darf. */
function alleSchluessel(name: string): string[] {
  const streng = namensteile(name).sort().join(' ')
  const umschrift = namensteileUmschrift(name).sort().join(' ')
  return streng === umschrift ? [streng] : [streng, umschrift]
}

export type Namensgrad = 'gleich' | 'aehnlich' | 'ungleich'

/**
 * Wie gut passen zwei Namen zueinander?
 *
 * - `gleich` – derselbe Bestand an Namensteilen. **Nur das** taugt zur
 *   automatischen Zuordnung, und auch dann nur, wenn es auf beiden Seiten
 *   eindeutig ist.
 * - `aehnlich` – plausibler Treffer (zweiter Vorname fehlt, Doppelname
 *   verkürzt). Wird **vorgeschlagen, nie gesetzt** – nach demselben Grundsatz
 *   wie beim Zusammenführen von Personen ([E-22](../../../docs/ENTSCHEIDUNGEN.md)):
 *   Namensgleichheit ist ein starker Hinweis und kein Beweis.
 * - `ungleich` – kein Zusammenhang erkennbar.
 */
export function namensgrad(a: string, b: string): Namensgrad {
  const teileA = namensteile(a)
  const teileB = namensteile(b)
  if (teileA.length === 0 || teileB.length === 0) return 'ungleich'

  const schluesselB = new Set(alleSchluessel(b))
  if (alleSchluessel(a).some((s) => schluesselB.has(s))) return 'gleich'

  const mengeB = new Set(teileB)
  const gemeinsam = teileA.filter((teil) => mengeB.has(teil))

  // Zwei gemeinsame Bestandteile sind bei einem Team dieser Größe ein deutlicher
  // Hinweis – „Anna Maria Berger" gegen „Anna Berger".
  if (gemeinsam.length >= 2) return 'aehnlich'

  /**
   * Ein gemeinsamer Bestandteil reicht nur, wenn er lang genug ist, um kein
   * Zufall zu sein, **und** der jeweils andere Teil als Abkürzung durchgeht
   * („M. Mustermann" gegen „Max Mustermann"). Ohne die zweite Bedingung wären
   * zwei „Michael" im Team ein Vorschlag – und genau davor warnt E-22.
   */
  const langGemeinsam = gemeinsam.find((teil) => teil.length >= 4)
  if (langGemeinsam) {
    const restA = teileA.filter((teil) => teil !== langGemeinsam)
    const restB = teileB.filter((teil) => teil !== langGemeinsam)
    const abkuerzung = restA.some((x) => restB.some((y) => x.startsWith(y) || y.startsWith(x)))
    if (abkuerzung) return 'aehnlich'
  }

  return 'ungleich'
}
